import type { Editor } from '@tiptap/react';
import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';

import type { WorkSurface } from '../../contracts';

export type WorkComposerSessionAppearance =
  | {
      kind: 'default';
      idlePlaceholder?: string;
      rotatingPlaceholder?: readonly MessageDescriptor[];
    }
  | { kind: 'landing' }
  | {
      kind: 'focused';
      idlePlaceholder: string;
      /**
       * Transient preview of the query a hovered Evidence starter would write.
       * Shows in place of `idlePlaceholder` while set, but never becomes the
       * composer's accessible name — a name that changed on mouse-move would
       * rename the textbox under a screen reader.
       */
      idlePlaceholderPreview?: string | null;
      toolbarControl: ReactNode;
      /**
       * Autocomplete sheet drawn under the composer card, supplied by the page.
       * It needs the live TipTap instance and has to stand down while the
       * composer's own `@` / `/` picker is open, so it arrives as a render
       * function rather than a node — the page never sees the editor otherwise,
       * and the composer never has to know what the sheet is.
       */
      renderSuggestionsOverlay?: (args: {
        editor: Editor | null;
        isPickerOpen: boolean;
        /** Report the sheet opening and closing so the card can square off the
         *  edge they share and the two read as one surface. */
        onVisibilityChange: (visible: boolean) => void;
      }) => ReactNode;
    };

export interface WorkComposerContextLock {
  patientName?: string;
  patientScoped: boolean;
}

export type ComposerHost =
  | { kind: 'home' }
  | { kind: 'explore' }
  | {
      kind: 'project';
      lock: WorkComposerContextLock | null;
      analyticsSurface?: WorkSurface;
    }
  | { kind: 'routine' };

const isBoundHost = (host: ComposerHost): boolean => host.kind === 'project';

export const composerAnalyticsSurface = (host: ComposerHost): WorkSurface | undefined =>
  host.kind === 'project' ? host.analyticsSurface : 'work_home';

export interface ComposerTestIds {
  card: string;
  glow: string;
  input: string;
  send: string;
  stop: string;
  dropOverlay: string;
  fileInput: string;
  linkedChips: string;
  attachments: string;
}

export const composerTestIds = (host: ComposerHost): ComposerTestIds =>
  isBoundHost(host)
    ? {
        card: 'project-chat-composer',
        glow: 'project-chat-composer-glow',
        input: 'project-chat-composer-input',
        send: 'project-chat-composer-send',
        stop: 'project-chat-composer-stop',
        dropOverlay: 'project-chat-drop-overlay',
        fileInput: 'project-chat-composer-file-input',
        linkedChips: 'project-chat-linked-chip',
        attachments: 'project-chat-composer-staged-files',
      }
    : {
        card:
          host.kind === 'home'
            ? 'projects-landing-composer-card'
            : 'projects-landing-composer',
        glow: 'projects-landing-composer-glow',
        input: 'projects-landing-composer-input',
        send: 'projects-landing-composer-send',
        stop: 'projects-landing-composer-stop',
        dropOverlay: 'projects-landing-drop-overlay',
        fileInput: 'projects-landing-composer-file-input',
        linkedChips: 'projects-landing-composer-linked-chips',
        attachments: 'projects-landing-composer-staged-files',
      };
